<template>
    <div>
        <team-users-controls
            v-model="fetchParams"
            @apply="applyFilters"
            @reset="resetFilters"
            @add="emit('add')"
            :loading="loading" :readOnly="props.readOnly"></team-users-controls>

        <page-loading v-if="loading && !list.length"></page-loading>

        <template v-if="!loading && !list.length">
            <empty-state v-if="!isEqual(defaultFilters(), fetchParams)" @reset="resetFilters" />
            <empty-state v-else-if="!list.length" class="mb-8">
                No users yet.
                <vx-button v-if="isAdminOrOwner && !props.readOnly" @click="emit('add')">
                    Add User <font-awesome-icon widthAuto :icon="faCirclePlus" />
                </vx-button>
            </empty-state>
        </template>

        <vx-table v-if="list.length" :class="FEATURES.contact_groups ? 'xl:grid-cols-5-auto' : 'xl:grid-cols-3-auto'" rowBreakpoint="xl">
            <vx-table-row isHeader>
                <div>
                    Name
                </div>
                <div>
                    Team Role
                </div>
                 <div v-if="FEATURES.contact_groups">
                    OU Access
                </div>
                <div v-if="FEATURES.contact_groups">
                    Group Access
                </div>
            </vx-table-row>

            <team-active-user-row
                v-for="(item, index) in list"
                v-model="list[index]"
                :key="list[index].id"
                :teamId="props.teamId"
                @addToGroup="openUserGroupModal($event)"
                @addToOrgUnit="openUserOrgUnitModal($event)"
                @deleted="removeFromList($event)" :readOnly="props.readOnly">
            </team-active-user-row>
        </vx-table>

        <div v-if="list.length" class="mt-8 flex justify-between w-full gap-10">
            <vx-button
                v-if="fetchParams.cursor"
                @click="loadData()"
                color="muted-dark"
                size="xl"
                data-test="load-more-btn"
                class="grow-0"
                :loading="loading">
                Load More
                <font-awesome-icon widthAuto :icon="faSpinner"></font-awesome-icon>
            </vx-button>
            <div class="text-lg grow-0 ml-auto font-normal text-slate-600">
                {{ list.length }}/{{ totalCount }}
            </div>
        </div>

        <user-group-modal v-if="FEATURES.contact_groups" :user="selectedUser" :teamId="props.teamId" v-model:visible="showUserGroupModal" @updatedGroupUser="onUpdateUserGroup"></user-group-modal>
        <user-org-unit-modal v-if="FEATURES.contact_groups" :user="selectedUser" :teamId="props.teamId" v-model:visible="showUserOrgUnitModal" @updatedOrgUnitUser="onUpdateUserOrgUnit"></user-org-unit-modal>

    </div>
</template>
<script setup>
import PageLoading from '~/components/general/PageLoading.vue'
import EmptyState from '~/components/general/EmptyState.vue';
import TeamActiveUserRow from './TeamActiveUserRow.vue';
import TeamUsersControls from './TeamUsersControls.vue';
import { usersClient } from '~/services';
import { VxButton, VxTable, VxTableRow } from '@voxie/frontend-components';
import { faCirclePlus, faSpinner } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useCursorPagination } from '~/composables/useCursorPagination';
import { inject, onMounted, ref } from 'vue';
import { isEqual } from '~/utils/helpers';
import UserGroupModal from './UserGroupModal.vue';
import UserOrgUnitModal from './UserOrgUnitModal.vue';

const props = defineProps({
    teamId: {
        type: Number,
        required: true,
    },
    readOnly: {
        type: Boolean,
        default: false,
    }
});

const isAdminOrOwner = Spark.isAdminOrOwner;
const FEATURES = inject('FEATURES');

const emit = defineEmits(['add']);

const totalCount = ref(0);
const showUserGroupModal = ref(false);
const showUserOrgUnitModal = ref(false);
const defaultFilters = () => ({
    cursor: null,
    sort_dir: 'desc',
    sort_field: 'id',
    per_page: 10,
    role: undefined,
    query: null,
});

const { list, loading, fetchParams, fetchRecords } = useCursorPagination();
const selectedUser = ref([]);

const resetFilters = () => {
    fetchParams.value = defaultFilters();

    loadData();
};

const applyFilters = () => {
    fetchParams.value = {
        ...fetchParams.value,
        cursor: null,
    }

    loadData();
}

const loadData = () => {
    fetchRecords(usersClient.listUsers(props.teamId, { ...fetchParams.value }));
    fetchTotalCount();
};

const fetchTotalCount = async () => {
    try {
        const response = await usersClient.usersCount(props.teamId, { ...fetchParams.value });
        totalCount.value = response.data?.count || 0;
    } catch (e) {
        console.error(e);
    }
}

const removeFromList = (itemId) => {
    list.value = list.value.filter((item) => item.id != itemId);
    totalCount.value = totalCount.value - 1;
    if (!list.value.length) {
        loadData();
    }
};

const openUserGroupModal = (user) => {
    selectedUser.value = { ...user };
    showUserGroupModal.value = true;
};

const onUpdateUserGroup = (event) => {
    list.value = list.value.map((user) => {
        if (user.id === event.user_id) {
            return {
                ...user,
                group_count: user.group_count + event.groups_added,
            };
        }
        return user;
    });
};

const openUserOrgUnitModal = (user) => {
    selectedUser.value = { ...user };
    showUserOrgUnitModal.value = true;
};

const onUpdateUserOrgUnit = (event) => {
    list.value = list.value.map((user) => {
        if (user.id === event.user_id) {
            return {
                ...user,
                org_unit_count: user.org_unit_count + event.org_units_added,
            };
        }
        return user;
    });
};

onMounted(() => {
    resetFilters();
});

</script>
